<template>
    <section class="min-w-0 rounded-xl border border-gray-200 overflow-hidden">
        <header class="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
            <div class="min-w-0">
                <p class="text-sm font-semibold text-gray-800">Draft preview</p>
                <p class="text-xs text-gray-500">Updates each time you continue.</p>
            </div>
            <button
                type="button"
                class="shrink-0 text-xs font-medium text-purple-700 hover:text-purple-900"
                :aria-expanded="!collapsed"
                aria-controls="rl-draft-preview"
                @click="toggle"
            >
                {{ collapsed ? 'Show' : 'Hide' }}
            </button>
        </header>

        <div v-show="!collapsed" id="rl-draft-preview" :class="busy ? 'opacity-60 transition-opacity' : 'transition-opacity'">
            <!--
                An iframe, not v-html, and that is not incidental: RocketLawyer
                ships the document with its own <style> block full of unscoped
                element selectors (h1, h2, table, ol, and `.htmlGenerated
                *:not(h1)`). Injected inline, those rules repaint the entire
                property page. The iframe also keeps the document byte-for-byte
                as RocketLawyer generated it, which is the point of a preview.

                sandbox: scripts, forms, popups and top-level navigation are all
                withheld. allow-same-origin is required — restoreScroll() reads
                contentWindow — and is safe precisely because allow-scripts is
                absent, so the usual sandbox-escape pairing does not apply.
            -->
            <iframe
                v-if="!withheldMessage && hasRendered && html"
                ref="frame"
                :srcdoc="framedHtml"
                sandbox="allow-same-origin"
                referrerpolicy="no-referrer"
                title="Draft document preview"
                class="h-[70vh] w-full border-0 bg-white"
                @load="restoreScroll"
            ></iframe>
            <!-- Withheld, not empty: we HAVE a document and are deliberately not
                 showing it, because it is RocketLawyer's guess rather than the
                 landlord's answer (RAC-10343). Distinct copy from the empty
                 state below, which means "nothing has arrived yet". -->
            <div v-else-if="withheldMessage" class="px-6 py-10 text-center">
                <svg class="w-8 h-8 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                <p class="text-sm text-gray-500 max-w-sm mx-auto">{{ withheldMessage }}</p>
            </div>
            <div v-else class="px-6 py-10 text-center text-sm text-gray-400">
                Your draft {{ documentLabel }} appears here as you answer.
            </div>
        </div>

        <p class="border-t border-gray-100 px-4 py-2 text-[11px] text-gray-400">Powered by Rocket Lawyer</p>
    </section>
</template>

<script setup>
/**
 * The live draft-preview pane beside the RocketLawyer interview questions
 * (RAC-10332). Renders the HTML RocketLawyer already inlines in the page
 * envelopes the wizard fetches — this component never talks to the network.
 *
 * Deliberately NOT labelled "live": it shows the answers RocketLawyer has, i.e.
 * as of the last Next/Back/Save, not what is currently being typed.
 */
import { computed, ref, watch } from 'vue'

import { applyWatermark } from './rocketLawyerPreview'

const props = defineProps({
    // Decoded document HTML, or null when we have not received one yet.
    html: {
        type: String,
        default: null,
    },
    // True while a page submit is in flight, so the pane can dim rather than
    // appear to change on its own.
    busy: {
        type: Boolean,
        default: false,
    },
    // "lease", "eviction notice" — used in the empty state only.
    documentLabel: {
        type: String,
        default: 'document',
    },
    // Set when the caller has a preview but has decided the landlord must not be
    // shown it yet — the eviction reason gate (RAC-10343). The string is the
    // explanation rendered in the pane's place; null shows the document as
    // normal. A prop rather than the caller passing html=null, so the wizard
    // keeps accumulating previews (and its change detection keeps working)
    // while they are withheld.
    withheldMessage: {
        type: String,
        default: null,
    },
})

// What actually goes into the iframe: RocketLawyer's document plus the draft
// watermark. Kept out of `html` itself so the wizard's change detection, the
// pre-flush scroll capture below, and the tests all keep working against the
// bytes RocketLawyer sent.
const framedHtml = computed(() => applyWatermark(props.html))

const STORAGE_KEY = 'rl-draft-preview-collapsed'
const DESKTOP_QUERY = '(min-width: 1280px)'

const frame = ref(null)

let pendingScroll = 0

const isDesktop = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false
    }
    return window.matchMedia(DESKTOP_QUERY).matches
}

const storedPreference = () => {
    try {
        const stored = window.localStorage?.getItem(STORAGE_KEY)
        if (stored === 'true') return true
        if (stored === 'false') return false
    } catch {
        // Private browsing / storage disabled — fall back to the viewport default.
    }
    return null
}

// Resolved during setup rather than in onMounted so the pane paints in its
// final state: a mounted-then-expanded pane flashes collapsed on every desktop
// page load. This app has no SSR entrypoint, so window is always available here.
const stored = storedPreference()
const collapsed = ref(stored === null ? !isDesktop() : stored)

// The iframe is mounted on first expand and then kept, never torn down:
// v-if alone would reparse ~55KB and lose scroll on every collapse/expand,
// while v-show alone would parse the whole document on phones where the pane
// starts collapsed and many landlords never open it.
const hasRendered = ref(!collapsed.value)

const toggle = () => {
    collapsed.value = !collapsed.value
    if (!collapsed.value) {
        hasRendered.value = true
    }
    try {
        window.localStorage?.setItem(STORAGE_KEY, String(collapsed.value))
    } catch {
        // Nothing to do — the toggle still works for this session.
    }
}

// Capture BEFORE the DOM updates. A pre-flush watcher still sees the OLD
// document in the iframe, so contentWindow.scrollY is the position the landlord
// was actually reading at; a post-flush watcher would read the new document,
// already reset to the top.
watch(() => props.html, () => {
    try {
        pendingScroll = frame.value?.contentWindow?.scrollY ?? 0
    } catch {
        pendingScroll = 0
    }
}, { flush: 'pre' })

const restoreScroll = () => {
    // Also skips the initial about:blank load, which fires here with nothing
    // worth restoring.
    if (pendingScroll <= 0) {
        return
    }

    const target = pendingScroll
    pendingScroll = 0

    // Consecutive pages differ only where a blank became an answer, so the
    // document barely reflows between them — restoring absolute pixels lands
    // the reader within a line or two of where they were.
    //
    // Two frames: the first lets the new document lay out, the second lets its
    // scroll height settle so the position is not clamped against a stale max.
    requestAnimationFrame(() => requestAnimationFrame(() => {
        try {
            // contentWindow, never window — scrolling the outer page on every
            // Next is the loudest way to get this wrong.
            frame.value?.contentWindow?.scrollTo(0, target)
        } catch {
            // A document swapped out mid-restore; the reader lands at the top.
        }
    }))
}
</script>
